import { useId, useState, type ComponentType, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './utils';

/**
 * Page-grammar primitive (SIL-538, W0 of
 * resources/plans/wayfinder-ux-consistency-spec.md §1.1-1.2): the
 * collapsible panel backing "Evidence and calculation details" and similar
 * disclose-on-demand sections — title + optional description in the
 * trigger, children only mounted while open.
 */
export function DisclosurePanel({
  title,
  description,
  icon: Icon,
  children,
  defaultOpen = false,
  className,
}: {
  title: string;
  description?: string;
  /** SIL-1227: optional lucide-react (or compatible) icon rendered before the
   * title, sized/aligned to match the trigger's chevron. Existing callers
   * that omit it render unchanged. */
  icon?: ComponentType<{ className?: string }>;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className={cn('rounded-lg border border-border', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          {Icon && <Icon className="size-4 shrink-0 text-muted-foreground" />}
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">{title}</span>
            {description && (
              <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>
            )}
          </span>
        </span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div id={panelId} className="border-t border-border px-4 py-3">
          {children}
        </div>
      )}
    </div>
  );
}
